package com.example.mystybag.sharedmodels.project.responseModels

import com.example.mystybag.sharedmodels.base.BaseResponseModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.BagAllergenItem
import kotlinx.serialization.Serializable
import kotlin.math.round
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.math.atan2
import kotlin.math.PI

@Serializable
data class BagWithTextsListResponse(
    var list: BagCategoryLists
): BaseResponseModel()

@Serializable
data class BagWithTextsAndMeta(
    val bagId                           : Long,
    var title                           : String? = null,
    val bagsCount                       : Int,
    val bagsReservationCount            : Int,
    val priceNormal                     : Double,
    val priceWithDiscount               : Double,
    val currency                        : String?,

    val merchantId                      : Long,
    val merchantTitle                   : String?,
    val merchantImageUrl                : String?,
    val merchantAllergens               : String?, // Legacy field, kept for backward compatibility
    val allergens                       : List<BagAllergenItem>? = null, // New field with enum
    val isFavorite                      : Boolean,

    val businessCategoryId              : Long?,
    val businessCategoryTitle           : String?,
    val businessCategoryDescription     : String?,

    val locationTitle                   : String?,
    val locationAddress                 : String?,
    val cityName                        : String?,
    val merchantLocationId             : Long? = null,

    val averageRating                   : Double?, // средна оценка за локацията
    val reviewCount                     : Int?,    // брой ревюта
    // The following 4 fields represent average ratings for the 4 rating categories
    // The meaning of each field depends on businessCategoryId:
    // - For food categories: category1=freshness/taste, category2=quality, category3=variety/portion_size, category4=value
    // - For service categories: category1=service_quality, category2=professionalism, category3=atmosphere/hygiene, category4=value
    // - etc. See ReviewCategoryConfigs for full mapping
    // Using @SerialName to maintain backward compatibility with existing API
    val ratingCategory1                 : Double?, // Average rating for first category (varies by business category)
    val ratingCategory2                 : Double?, // Average rating for second category (varies by business category)
    val ratingCategory3                 : Double?, // Average rating for third category (varies by business category)
    val ratingCategory4                 : Double?, // Average rating for fourth category (varies by business category)
    val collectWindowLabel              : String,  // "collect tomorrow 08:00 - 09:00"
    val locationLatitude                : Double,
    val locationLongitude               : Double,
    val coverPhotoUrl                   : String?,
    /** Resolved products in this bag (name + grams). */
    val products                        : List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductModel> = emptyList(),
    val dateTimeAdded                   : Long? = null,
    val containsProductsForPersonsOver18: Boolean = false,
)

fun BagWithTextsAndMeta.toHomeTopPickUiModel(
    userLatitude: Double? = null,
    userLongitude: Double? = null
): HomeTopPickUiModel {
    val available = (this.bagsCount - this.bagsReservationCount).coerceAtLeast(0)
    
    // Calculate distance - only use client-side calculation if we have user location
    // If no user location, don't show distance at all
    val finalDistanceMeters: Int? = if (userLatitude != null && userLongitude != null &&
            this.locationLatitude != 0.0 && this.locationLongitude != 0.0) {
        calculateDistanceMeters(
            userLatitude, userLongitude,
            this.locationLatitude, this.locationLongitude
        )
    } else {
        null // No user location - don't show distance
    }
    
    return HomeTopPickUiModel(
        id = this.bagId.toString(),
        locationCoverUrl =  this.coverPhotoUrl,
        merchantImageUrl = this.merchantImageUrl,
        bagsCount = this.bagsCount,
        bagsReservationCount = this.bagsReservationCount,
        bagsAvailable = available,

        isSellingFast = (this.bagsReservationCount >= this.bagsCount * 0.7),
        // пример: ако 70% вече са резервирани

        rating = this.averageRating ?: 0.0,

        // Card title: prefer bag title when set, then location name, then merchant name
        title = this.title?.takeIf { it.isNotBlank() } ?: this.locationTitle ?: this.merchantTitle ?: "",
        subtitle = this.businessCategoryTitle ?: "",
        bagTitle = this.title,

        collectInfo = this.collectWindowLabel,

        distance = finalDistanceMeters?.let { formatDistance(it) } ?: "",

        oldPrice = formatPriceKmp(this.priceNormal, this.currency),
        newPrice = formatPriceKmp(this.priceWithDiscount, this.currency),
        priceWithDiscountNumeric = this.priceWithDiscount,

        isFavorite = isFavorite,

        merchantId  = merchantId,
        merchantLocationId = this.merchantLocationId,
        locationLatitude = locationLatitude,
        locationLongitude = locationLongitude,
        
        // Rating details
        reviewCount = this.reviewCount,
        ratingCategory1 = this.ratingCategory1,
        ratingCategory2 = this.ratingCategory2,
        ratingCategory3 = this.ratingCategory3,
        ratingCategory4 = this.ratingCategory4,
        
        // Additional bag details
        merchantAllergies = this.merchantAllergens,
        allergens = this.allergens,
        locationTitle = this.locationTitle,
        locationAddress = this.locationAddress,
        cityName = this.cityName,
        businessCategoryTitle = this.businessCategoryTitle,
        businessCategoryDescription = this.businessCategoryDescription,
        businessCategoryId          = this.businessCategoryId,
        products = this.products,
        dateTimeAddedEpochMs = this.dateTimeAdded,
        containsProductsForPersonsOver18 = this.containsProductsForPersonsOver18
    )
}

fun formatPriceKmp(price: Double, currency: String?): String {
    val rounded = round(price * 100) / 100
    val parts = rounded.toString().split(".")
    val integer = parts[0]
    val decimals = if (parts.size > 1) parts[1].padEnd(2, '0') else "00"

    val value = "$integer,$decimals"   // запетая по BG формат
    val suffix = when {
        currency == null -> null
        currency.equals("EUR", ignoreCase = true) -> "€"
        else -> currency
    }
    return if (suffix != null) "$value $suffix" else value
}

/**
 * Calculates the distance between two coordinates using the Haversine formula.
 * Returns distance in meters.
 */
fun calculateDistanceMeters(
    lat1: Double,
    lon1: Double,
    lat2: Double,
    lon2: Double
): Int {
    val earthRadiusMeters = 6371000.0 // Earth radius in meters
    
    // Convert degrees to radians
    fun toRadians(degrees: Double): Double = degrees * PI / 180.0
    
    val dLat = toRadians(lat2 - lat1)
    val dLon = toRadians(lon2 - lon1)
    
    val a = sin(dLat / 2) * sin(dLat / 2) +
            cos(toRadians(lat1)) * cos(toRadians(lat2)) *
            sin(dLon / 2) * sin(dLon / 2)
    
    val c = 2 * atan2(sqrt(a), sqrt(1 - a))
    
    return (earthRadiusMeters * c).toInt()
}

fun formatDistance(meters: Int): String {
    return when {
        meters < 1000 -> "$meters m"
        else -> {
            val kilometers = meters / 1000.0
            val roundedKm = round(kilometers * 10) / 10.0
            // Format to 1 decimal place, removing trailing zero if it's a whole number
            val formattedKm = if (roundedKm % 1.0 == 0.0) {
                roundedKm.toInt().toString()
            } else {
                // Convert to string and ensure only one decimal place
                // Handle both '.' and ',' as decimal separators
                val kmStr = roundedKm.toString().replace(',', '.')
                val dotIndex = kmStr.indexOf('.')
                if (dotIndex >= 0 && dotIndex < kmStr.length - 2) {
                    kmStr.substring(0, dotIndex + 2) // Take up to 1 decimal place
                } else {
                    kmStr
                }
            }
            "$formattedKm km"
        }
    }
}

data class HomeTopPickUiModel(
    val id              : String,
    val merchantId      : Long,
    val merchantLocationId: Long? = null,
    val locationCoverUrl       : String? = null,
    val merchantImageUrl: String? = null,
    val bagsCount       : Int = 0,
    val bagsReservationCount: Int = 0,
    val bagsAvailable   : Int = 0,
    val isSellingFast   : Boolean = false,
    val rating          : Double = 0.0,
    val title           : String,
    val subtitle        : String,          // напр. "Surprise Bag"
    val collectInfo     : String,       // напр. "Collect today 19:30 - 20:00"
    val distance        : String,          // напр. "41 m"
    val oldPrice        : String,          // напр. "12,00 €"
    val newPrice        : String,          // напр. "4,00 €",
    /** Numeric price for local filtering (discounted price). */
    val priceWithDiscountNumeric: Double? = null,
    val isFavorite      : Boolean = false,
    val locationLatitude: Double,
    val locationLongitude: Double,
    // Rating details (optional, for bag detail page)
    // These fields represent average ratings for the 4 rating categories
    // The meaning depends on businessCategoryId (see ReviewCategoryConfigs)
    val reviewCount                     : Int? = null,
    val ratingCategory1                 : Double? = null, // Average rating for first category (varies by business category)
    val ratingCategory2                 : Double? = null, // Average rating for second category (varies by business category)
    val ratingCategory3                 : Double? = null, // Average rating for third category (varies by business category)
    val ratingCategory4                 : Double? = null, // Average rating for fourth category (varies by business category)
    // Additional bag details (optional, for bag detail page)
    val merchantAllergies               : String? = null, // Legacy field
    val allergens                       : List<BagAllergenItem>? = null,
    val locationTitle                   : String? = null, // Title of the merchant location
    val locationAddress                 : String? = null,
    val cityName                        : String? = null,
    val businessCategoryId              : Long?,
    val businessCategoryTitle           : String? = null,
    val businessCategoryDescription     : String? = null,
    val bagTitle                        : String? = null, // Title of the bag (from bag.title)
    /** Resolved products in this bag (name + grams). Replaces the legacy free-text field. */
    val products                        : List<com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductModel> = emptyList(),
    /** Date when the offer was added (epoch ms). Used for "offer published date" filter. */
    val dateTimeAddedEpochMs            : Long? = null,
    val containsProductsForPersonsOver18: Boolean = false,
)

@Serializable
data class BagCategoryLists(
    val allBags          : List<BagWithTextsAndMeta>,
    val bakery           : List<BagWithTextsAndMeta> = emptyList(),  // "Пекарни"
    val confectionery    : List<BagWithTextsAndMeta> = emptyList(),  // "Сладкарници"
    val fishMarket       : List<BagWithTextsAndMeta> = emptyList(),  // "Рибарници"
    val meals            : List<BagWithTextsAndMeta> = emptyList()   // "Храна за вкъщи"
)